import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Alerta, Badge, Boton, Cargando, Pestanas, Tabla } from '../../components/kit'
import { useGuias, useTeletrabajoVigentes } from '../../queries/useInventario'
import { GuiaDespacho } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { diaLocal, formatoDia, formatoDiaSemana, isoDia } from '../../utils/fecha'
import { nombrarDias, SENTIDO } from '../../utils/teletrabajo'

const moverSemana = (iso: string, semanas: number) => {
  const d = diaLocal(iso)
  d.setDate(d.getDate() + 7 * semanas)
  return isoDia(d)
}

/**
 * Teletrabajo: las guías de despacho de la semana (para emitirlas en la otra plataforma)
 * y las fichas vigentes. Las guías salen de las fichas: cada bien sale el día hábil antes
 * del teletrabajo y vuelve el siguiente; una guía junta los bienes de una persona en un día.
 */
export default function Teletrabajo() {
  const [params, setParams] = useSearchParams()
  const semana = params.get('semana') || isoDia(new Date())
  const [pestana, setPestana] = useState<'guias' | 'fichas'>('guias')
  const guias = useGuias(semana)
  const fichas = useTeletrabajoVigentes()
  const irA = (iso: string | null) => setParams(iso ? { semana: iso } : {}, { replace: true })

  const porDia = new Map<string, GuiaDespacho[]>()
  guias.data?.guias.forEach((g) => porDia.set(g.fecha, [...(porDia.get(g.fecha) ?? []), g]))

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Teletrabajo</h1>
        <p className="texto-secundario">
          Los bienes que se llevan a la casa. Cada semana: una guía de salida el día antes del teletrabajo y una de entrada el día después.
          Las fichas se arman desde la ficha de cada bien.
        </p>
      </header>

      <Pestanas
        etiqueta="Teletrabajo"
        activa={pestana}
        onCambiar={(c) => setPestana(c as 'guias' | 'fichas')}
        pestanas={[
          { clave: 'guias', texto: 'Guías de la semana' },
          { clave: 'fichas', texto: `Fichas vigentes${fichas.data ? ` (${fichas.data.length})` : ''}` },
        ]}
      >
        {pestana === 'guias' && (
          <div className="pila">
            <div className="semana">
              <Boton variante="outlined" tam="sm" icono={ChevronLeft} onClick={() => irA(moverSemana(semana, -1))} aria-label="Semana anterior" />
              <p className="semana__rango">
                {guias.data ? `${formatoDiaSemana(guias.data.inicio)} al ${formatoDia(guias.data.fin)}` : '…'}
              </p>
              <Boton variante="outlined" tam="sm" icono={ChevronRight} onClick={() => irA(moverSemana(semana, 1))} aria-label="Semana siguiente" />
              {params.get('semana') && <Boton variante="text" tam="sm" onClick={() => irA(null)}>Esta semana</Boton>}
            </div>
            {guias.isLoading && <Cargando />}
            {guias.isError && <Alerta tipo="error">{mensajeDeError(guias.error)}</Alerta>}
            {guias.data && !guias.data.guias.length && <p className="texto-secundario">No hay guías esta semana.</p>}
            {[...porDia].map(([fecha, delDia]) => (
              <section key={fecha} className="pila">
                <h2 className="dcc-t-heading-s guias__dia">{formatoDiaSemana(fecha)}</h2>
                <ul className="guias">
                  {delDia.map((g) => (
                    <TarjetaGuia key={`${g.sentido}-${g.persona.id}`} guia={g} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        {pestana === 'fichas' && (
          <div className="pila">
            {fichas.isLoading && <Cargando />}
            {fichas.isError && <Alerta tipo="error">{mensajeDeError(fichas.error)}</Alerta>}
            {fichas.data && !fichas.data.length && <p className="texto-secundario">Nadie se lleva bienes a la casa.</p>}
            {fichas.data && fichas.data.length > 0 && (
              <Tabla etiqueta="Fichas de teletrabajo vigentes" apilable>
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Bien</th>
                    <th>Teletrabaja</th>
                    <th>Desde</th>
                  </tr>
                </thead>
                <tbody>
                  {fichas.data.map((f) => (
                    <tr key={f.id}>
                      <td data-etiqueta="Persona">
                        {f.persona.nombre}
                        <span className="tabla__sub">{f.persona.rut}</span>
                      </td>
                      <td data-etiqueta="Bien">
                        <Link to={`/app/bienes/${f.bien.id}`} className="tabla__enlace">{f.bien.numero || 'Sin N°'}</Link> {f.bien.tipo_equipo}
                        <span className="tabla__sub">{f.bien.recinto ?? 'Sin ubicación'}</span>
                      </td>
                      <td data-etiqueta="Teletrabaja">{nombrarDias(f.dias)}</td>
                      <td data-etiqueta="Desde">{formatoDia(f.desde)}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
            )}
          </div>
        )}
      </Pestanas>
    </div>
  )
}

/** Lo que pide la guía: la persona y su domicilio, y cada equipo (tipo, marca, modelo, serie). */
function TarjetaGuia({ guia }: { guia: GuiaDespacho }) {
  const p = guia.persona
  return (
    <li className="guia">
      <div className="guia__cabecera">
        <Badge tono={SENTIDO[guia.sentido].tono}>{SENTIDO[guia.sentido].texto}</Badge>
        <strong>{p.nombre}</strong>
      </div>
      <dl className="datos guia__datos">
        <dt>RUT</dt>
        <dd>{p.rut || '—'}</dd>
        <dt>Dirección</dt>
        <dd>{p.direccion || '—'}</dd>
        <dt>Comuna</dt>
        <dd>{p.comuna || '—'}</dd>
        <dt>Ciudad</dt>
        <dd>{p.ciudad || '—'}</dd>
        <dt>Correo</dt>
        <dd>{p.email || '—'}</dd>
      </dl>
      <Tabla etiqueta={`Equipos de la guía de ${p.nombre}`} apilable>
        <thead>
          <tr>
            <th>Tipo de equipo</th>
            <th>Marca</th>
            <th>Modelo</th>
            <th>N° de serie</th>
            <th>N° de bien</th>
          </tr>
        </thead>
        <tbody>
          {guia.bienes.map((b) => (
            <tr key={b.id}>
              <td data-etiqueta="Tipo de equipo">{b.tipo_equipo}</td>
              <td data-etiqueta="Marca">{b.marca || '—'}</td>
              <td data-etiqueta="Modelo">{b.modelo || '—'}</td>
              <td data-etiqueta="N° de serie">{b.serie || '—'}</td>
              <td data-etiqueta="N° de bien">
                <Link to={`/app/bienes/${b.id}`}>{b.numero || 'Sin N°'}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </Tabla>
    </li>
  )
}
